import React, { useState, useMemo } from 'react';
import cn from 'classnames';
import PropTypes from 'prop-types';
import GeneralDialogLayout from '@Containers/Dialogs/GeneralDialogLayout/GeneralDialogLayout';
import { Button, ButtonWithIcon } from '@els/els-react--button';
import SimpleSpinner from '@Components/Results/utils/SimpleSpinner';
import { TabGroup, TabPanel } from '@els/els-react--tab-group';
import { AddStepsDialogTabs, DEFAULT_DATA_PAGE_SIZE } from '@Pages/RetroSynthesis/EditPlan/AddSteps/constants';
import PlanPublishedItems from '@Pages/RetroSynthesis/EditPlan/AddSteps/components/PlanPublishedItems';
import PlanPredictedItems from '@Pages/RetroSynthesis/EditPlan/AddSteps/components/PlanPredictedItems';
import StructureToBase64 from '@Containers/StructureDialog/StructureToBase64';
import SelectAllItemsControls from '@Containers/ResultToolbar/SelectedItemsControls/SelectAllItemsControls';
import { getSelectionPropsForSelectAll } from '@Pages/RetroSynthesis/EditPlan/AddSteps/utils/selection';
import PlanNoResults from '@Pages/RetroSynthesis/EditPlan/AddSteps/components/PlanNoResults';
import { PaginationResponseParams } from '@Pages/RetroSynthesis/constants';
import get from 'lodash/get';
import { useResults } from '@Pages/RetroSynthesis/EditPlan/AddSteps/components/hooks';

import './AddStepsDialog.scss';

function AddStepsDialog({
  isPredictedEnabled,
  isLoading,
  selectedStep,
  onClose,
  published,
  fetchPublishedData,
  predicted,
  isPredictedLoading,
  isPublishedLoading,
  fetchPredictedData,
  selectedStepExamplesSearchData,
  onTargetStructureClick,
  onAddStep,

  // Selection
  selectedItemsByTab,
  setSelectedItemsByTab,

  // In-house
  reaxysDatabaseId,
  inHouseDatabases,
  fetchInhouseData,
  isInHouseLoadingByDatabase,
  inHouseResponseByDatabase,
  ...rest
}) {
  const [activeTab, setActiveTab] = useState(AddStepsDialogTabs.PUBLISHED);
  const { tabs, labels } = useMemo(() => {
    const predefinedTabs = isPredictedEnabled ? AddStepsDialogTabs.allTabs : [AddStepsDialogTabs.PUBLISHED];
    const tabsV = [...predefinedTabs, ...(inHouseDatabases ? inHouseDatabases.map((db) => db.id) : [])];
    const labelsV = {
      ...predefinedTabs.reduce(
        (memo, tab) => ({
          ...memo,
          [tab]: tab,
        }),
        {}
      ),
      ...(inHouseDatabases &&
        inHouseDatabases.reduce(
          (memo, db) => ({
            ...memo,
            [db.id]: db.name,
          }),
          {}
        )),
    };
    return { tabs: tabsV, labels: labelsV };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inHouseDatabases]);

  const onTabChange = (tab) => {
    setActiveTab(tab);
  };
  const onAddStepClick = () => {
    onAddStep();
  };

  const getTabData = (tab) => {
    switch (tab) {
      case AddStepsDialogTabs.PUBLISHED:
        return get(published, 'content');
      case AddStepsDialogTabs.PREDICTED:
        return get(predicted, 'content');
      default:
        return inHouseResponseByDatabase && inHouseResponseByDatabase[tab] && get(inHouseResponseByDatabase[tab], 'content');
    }
  };

  const getTabLabel = (tab) => labels[tab];

  const getTabCount = (tab) => {
    switch (tab) {
      case AddStepsDialogTabs.PUBLISHED:
        return get(published, PaginationResponseParams.TOTAL_ELEMENTS, '...');
      case AddStepsDialogTabs.PREDICTED:
        return get(predicted, PaginationResponseParams.TOTAL_ELEMENTS, '...');
      default:
        return inHouseResponseByDatabase && inHouseResponseByDatabase[tab]
          ? get(inHouseResponseByDatabase[tab], PaginationResponseParams.TOTAL_ELEMENTS, '...')
          : '...';
    }
  };

  const predictedExamplesResults = useResults(selectedStepExamplesSearchData);

  // eslint-disable-next-line sonarjs/cognitive-complexity
  const getTabContent = (tab) => {
    const items = getTabData(activeTab);
    switch (tab) {
      case AddStepsDialogTabs.PUBLISHED: {
        if (isPublishedLoading) {
          return <SimpleSpinner />;
        }
        return published?.totalElements === 0 ? (
          <PlanNoResults />
        ) : (
          <PlanPublishedItems
            key={AddStepsDialogTabs.PUBLISHED}
            databaseId={reaxysDatabaseId}
            items={items}
            selectedItems={selectedItemsByTab[AddStepsDialogTabs.PUBLISHED]}
            setSelectedItems={(selectedItems) => setSelectedItemsByTab(AddStepsDialogTabs.PUBLISHED, selectedItems)}
            pagination={published}
            fetchData={fetchPublishedData}
          />
        );
      }
      case AddStepsDialogTabs.PREDICTED: {
        if (isPredictedLoading) {
          return <SimpleSpinner />;
        }
        return predicted?.totalElements === 0 ? (
          <PlanNoResults />
        ) : (
          <PlanPredictedItems
            items={items}
            selectedStepExamplesSearchData={selectedStepExamplesSearchData}
            selectedItems={selectedItemsByTab[AddStepsDialogTabs.PREDICTED]}
            setSelectedItems={(selectedItems) => setSelectedItemsByTab(AddStepsDialogTabs.PREDICTED, selectedItems)}
            predictedExamplesResults={predictedExamplesResults}
            pagination={predicted}
            fetchData={fetchPredictedData}
          />
        );
      }
      default: {
        if (isInHouseLoadingByDatabase[activeTab]) {
          return <SimpleSpinner />;
        }
        return inHouseResponseByDatabase && inHouseResponseByDatabase[activeTab]?.totalElements === 0 ? (
          <PlanNoResults />
        ) : (
          <PlanPublishedItems
            key={activeTab}
            databaseId={activeTab}
            items={items}
            selectedItems={selectedItemsByTab[activeTab]}
            fetchData={(params) => fetchInhouseData(activeTab, params)}
            pagination={inHouseResponseByDatabase[activeTab]}
            setSelectedItems={(selectedItems) => setSelectedItemsByTab(activeTab, selectedItems)}
            isInHouse
          />
        );
      }
    }
  };

  // Selection
  const { allItemsSelected, currentSelectedItems, someItemsSelected, checkAllItems } = getSelectionPropsForSelectAll({
    selectedItemsByTab,
    setSelectedItemsByTab,
    activeTab,
    activeTabData: getTabData(activeTab),
  });
  const renderTargetThumbnail = ({ src }) => (
    <Button
      className="target-thumbnailp edit-plan-popup__target-thumbnail-button"
      type="tertiary"
      data-e2e="target-structure-button"
      onClick={() =>
        onTargetStructureClick(
          selectedStep.substance?.structure,
          selectedStep.substance?.subDetails?.chemicalNames?.[0] || selectedStep.substance?.subDetails?.linearFormula
        )
      }>
      <img src={src} alt="Building blocks to target structure" className="edit-plan-popup__target-thumbnail-image" />
    </Button>
  );

  return (
    <GeneralDialogLayout
      {...rest}
      size="xl"
      movable
      closeBtn={false}
      resizable
      className="react-dialogs-scrollable edit-plan-popup"
      headerClassName="edit-plan-popup__header"
      header={
        <>
          <h2>Add reaction steps (one step only)</h2>
          <ButtonWithIcon
            type="link"
            size="small"
            sprite="Close"
            data-e2e="add-step-modal-close-button"
            linkIconSize="xs"
            iconOnly
            onClick={onClose}
            className="els-btn-modal-close e2e-modal-close">
            Close
          </ButtonWithIcon>
        </>
      }
      selfClose={onClose}>
      <div className="edit-plan-popup__container">
        {isLoading && <SimpleSpinner />}

        <div className="edit-plan-popup__container__header u-els-margin-top u-els-margin-left">
          <SelectAllItemsControls
            allItemsSelected={allItemsSelected}
            currentSelectedItems={currentSelectedItems}
            someItemsSelected={someItemsSelected}
            checkAllItems={checkAllItems}
          />
          <Button onClick={onAddStepClick} disabled={!currentSelectedItems} data-e2e="limit-to-button" className="e2e-limit-to">
            Add
          </Button>
          {selectedStep.substance && (
            <StructureToBase64 structure={selectedStep.substance?.structure} customRenderer={renderTargetThumbnail} />
          )}
        </div>
        <TabGroup
          className="u-els-margin-left u-els-margin-top"
          tabs={tabs && tabs.map((tab) => `${getTabLabel(tab)} (${getTabCount(tab)})`)}
          onTabChange={(s) => {
            onTabChange(tabs[s]);
          }}>
          {tabs &&
            tabs.map((item) => (
              <TabPanel key={item}>
                <div
                  className={cn('edit-plan-popup__tabs-content', {
                    'edit-plan-popup__tabs-content-paginated': getTabCount(item) > DEFAULT_DATA_PAGE_SIZE,
                  })}>
                  {getTabContent(item)}
                </div>
              </TabPanel>
            ))}
        </TabGroup>
      </div>
    </GeneralDialogLayout>
  );
}

AddStepsDialog.defaultProps = {
  onTargetStructureClick: () => null,
  selectedItemsByTab: {},
  setSelectedItemsByTab: () => {},
  isInHouseLoadingByDatabase: {},
  inHouseResponseByDatabase: {},
  fetchPublishedData: () => null,
  fetchInhouseData: () => null,
};

AddStepsDialog.propTypes = {
  isPredictedEnabled: PropTypes.bool,
  isLoading: PropTypes.bool,
  selectedStep: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  onTargetStructureClick: PropTypes.func,
  onAddStep: PropTypes.func.isRequired,
  published: PropTypes.shape({
    content: PropTypes.array,
    number: PropTypes.number,
    size: PropTypes.number,
    totalElements: PropTypes.number,
    totalPages: PropTypes.number,
  }),
  fetchPublishedData: PropTypes.func,
  fetchPredictedData: PropTypes.func,
  predicted: PropTypes.shape({
    content: PropTypes.array,
    number: PropTypes.number,
    size: PropTypes.number,
    totalElements: PropTypes.number,
    totalPages: PropTypes.number,
  }),
  isPredictedLoading: PropTypes.bool,
  isPublishedLoading: PropTypes.bool,
  selectedStepExamplesSearchData: PropTypes.object,

  // Selection
  selectedItemsByTab: PropTypes.object,
  setSelectedItemsByTab: PropTypes.func,

  // In-house
  reaxysDatabaseId: PropTypes.string,
  inHouseDatabases: PropTypes.array,
  fetchInhouseData: PropTypes.func,
  isInHouseLoadingByDatabase: PropTypes.object,
  inHouseResponseByDatabase: PropTypes.object,
};

export default AddStepsDialog;
